import { isRtSplitViewEnabledSector } from '@/src/constants/srp/rtSplitView'
import { useIsInternalUser } from '@/src/contexts/InternalUserContext'
import { MulticitySearchBody, QueryInput } from '@/src/types/searchQuery'
import { SrpViewMode } from '@/src/types/srpJourneyIndex'
import { validSearchQueryDateFormats } from '@/src/utils/date'
import { getMulticitySearchBody, getSearchQueryObj, isMulticityQuery } from '@/src/utils/srp/searchUtils'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { useGetSearchParamsObj } from '../searchParams'

dayjs.extend(customParseFormat)

const useGetSearchQueryURL: () => {
    query: string
    error: null | 'dateInPast' | 'requiredFieldsMissing' | 'roundTripLikeMulticity'
    /** Single source of truth for the active view. The booleans below are derived from it. */
    srpViewMode?: SrpViewMode
    isRT?: boolean
    isSolutionView?: boolean
    isRTSplitView?: boolean
    applyInitialStopsFilter?: string | null
    isPerPaxPricingRequired?: boolean
    isMulticity?: boolean
    multicityBody?: MulticitySearchBody
    journeyCount?: number
    isRoundTripLikeMulticity?: boolean
} = () => {
    const { urlQueryObj } = useGetSearchParamsObj()
    // Must stay above the early returns below — hooks can't be called conditionally.
    const isInternalUser = useIsInternalUser()

    const departDate = urlQueryObj?.depart_date
    const returnDate = urlQueryObj?.return_date
    const adults = Number(urlQueryObj?.adults)
    const from = urlQueryObj?.from
    const to = urlQueryObj?.to

    // Helper function to parse date with flexible format
    const parseDate = (dateStr: string) => {
        if (!dateStr?.trim()) return null

        // Try different date formats - all combinations of single/double digit day and month

        for (const format of validSearchQueryDateFormats) {
            const parsed = dayjs(dateStr, format)
            if (parsed.isValid()) {
                return parsed
            }
        }

        return null
    }

    // Multicity search uses a different URL contract (segments) and a POST body.
    if (isMulticityQuery(urlQueryObj)) {
        const multicityBody = getMulticitySearchBody(urlQueryObj)
        const hasValidSectors =
            multicityBody.sectors.length >= 2 &&
            multicityBody.sectors.every(sector => sector.from && sector.to && sector.depart_date)

        if (!multicityBody.adults || !hasValidSectors) {
            return { query: '', error: 'requiredFieldsMissing', isMulticity: true }
        }

        const hasPastSector = multicityBody.sectors.some(sector => {
            const parsed = parseDate(sector.depart_date)
            return parsed && parsed.isBefore(dayjs(), 'day')
        })
        if (hasPastSector) {
            return { query: '', error: 'dateInPast', isMulticity: true }
        }

        // Detect a round-trip disguised as 2-segment multicity: J1 A→B, J2 B→A.
        // Return a blocking error so both API queries are skipped; the SRP container
        // handles this case by showing a dialog instead of the error screen.
        const isRoundTripLikeMulticity =
            multicityBody.sectors.length === 2 &&
            multicityBody.sectors[0].from === multicityBody.sectors[1].to &&
            multicityBody.sectors[0].to === multicityBody.sectors[1].from

        if (isRoundTripLikeMulticity) {
            return {
                query: '',
                error: 'roundTripLikeMulticity' as const,
                isMulticity: true,
                multicityBody,
                journeyCount: multicityBody.sectors.length,
                isRoundTripLikeMulticity: true
            }
        }

        return {
            query: '',
            error: null,
            srpViewMode: 'multicity',
            isRT: false,
            isSolutionView: false,
            isRTSplitView: false,
            isPerPaxPricingRequired: false,
            isMulticity: true,
            multicityBody,
            journeyCount: multicityBody.sectors.length,
            isRoundTripLikeMulticity: false
        }
    }

    //mandatory fields for search
    if (!adults || !from || !to || !departDate) {
        return {
            query: '',
            error: 'requiredFieldsMissing'
        }
    }

    //check  date is not in the past
    const parsedDepartDate = parseDate(departDate)
    const parsedReturnDate = parseDate(returnDate)
    const isDepartDateInPast = parsedDepartDate && parsedDepartDate.isBefore(dayjs(), 'day')
    const isReturnDateInPast = parsedReturnDate && parsedReturnDate.isBefore(dayjs(), 'day')
    if (isDepartDateInPast || isReturnDateInPast) {
        return {
            query: '',
            error: 'dateInPast'
        }
    }
    const searchQueryObj = getSearchQueryObj(urlQueryObj as QueryInput)
    const applyInitialStopsFilter = urlQueryObj.nonstop?.trim()
    //adding perPaxPricingRequired
    const isPerPaxPricingRequired = !parsedReturnDate
    searchQueryObj.perPaxPricingRequired = isPerPaxPricingRequired.toString()

    const isRT = !!returnDate

    // Split-view rollout gate: live for every internal user on every sector, and for public
    // traffic only on the phase-1 sectors (see RT_SPLIT_VIEW_ENABLED_SECTORS).
    const RT_SPLIT_VIEW_ENABLED = isInternalUser || isRtSplitViewEnabledSector(from, to)
    const srpViewMode: SrpViewMode = isRT ? (RT_SPLIT_VIEW_ENABLED ? 'rtSplit' : 'rtSolution') : 'ow'
    const isRTSplitView = srpViewMode === 'rtSplit'
    searchQueryObj.rtsplitview = isRTSplitView ? 'true' : 'false'
    const query = new URLSearchParams(searchQueryObj).toString()
    return {
        query,
        error: null,
        srpViewMode,
        isRT,
        isSolutionView: !isRTSplitView,
        isRTSplitView,
        applyInitialStopsFilter,
        isPerPaxPricingRequired
    }
}
export default useGetSearchQueryURL
